import { useEffect, useState } from 'react'
import { DesignStudioEditor } from './DesignStudioEditor.tsx'
import { designApi } from '../infrastructure/api/designApi'
import type { CreateDesignResponse } from '../infrastructure/api/designApi'
import type { EmbedAction } from '../shared/embed'
import { useIntlayer } from 'react-intlayer'

type DesignStudioLoaderProps = {
  action: EmbedAction
  targetSelector: string
}

/**
 * Design Studio Loader
 * Handles loading design data based on action and passes it to the editor
 */
export const DesignStudioLoader = ({
  action,
  targetSelector: _targetSelector,
}: DesignStudioLoaderProps) => {
  const { loadingDesign, error: errorText } = useIntlayer('designStudioLoader')
  const [design, setDesign] = useState<CreateDesignResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadDesign = async () => {
      try {
        setLoading(true)
        setError(null)

        let result: CreateDesignResponse

        if (action.type === 'create') {
          // Always create a new design from API
          console.log(
            'Creating new design for cart/product:',
            action.payload.cartId,
            action.payload.productId,
          )
          result = await designApi.createDesign({
            cartId: action.payload.cartId,
            productId: action.payload.productId,
            variationId: action.payload.variationId,
            templateName: action.payload.templateName,
            language: action.payload.language,
          })
        } else if (action.type === 'open') {
          result = await designApi.loadDesign(action.payload.designId)
        } else {
          throw new Error(`Unknown action type: ${(action as any).type}`)
        }

        setDesign(result)
      } catch (err) {
        console.error('Failed to load design:', err)
        setError(err instanceof Error ? err.message : 'Failed to load design')
      } finally {
        setLoading(false)
      }
    }

    loadDesign()
  }, [action])

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">{loadingDesign}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            {errorText}
            {error}
          </p>
        </div>
      </div>
    )
  }

  if (!design) {
    return null
  }

  return <DesignStudioEditor design={design} />
}
